<script lang="ts" setup>
import type { MarketingBadge, MarketingFlag, OfferFull, OfferMedium } from '~/types/Catalog';
import { isFreeDeliveryMarketingFlag, shouldShowFreeDeliveryFlag } from '~/utils/freeDeliveryFlag';

const { isFreeDeliveryAvailable, offer, usedIn } = defineProps<{
  isFreeDeliveryAvailable?: boolean;
  offer: OfferMedium | OfferFull;
  usedIn: 'category' | 'offer';
}>();

const selectedFlag = ref<MarketingFlag | null>(null);
const isShowFlagModal = ref(false);

watch(
  () => selectedFlag.value,
  () => (isShowFlagModal.value = !!selectedFlag.value),
);

function checkFlagDisplay(flag: MarketingFlag): boolean {
  switch (usedIn) {
    case 'category':
      return flag.show_in_categories ?? true;
    case 'offer':
      return flag.show_in_offer ?? true;
    default:
      return true;
  }
}

const freeDelivery = computed(() => offer.marketing_flags.find(isFreeDeliveryMarketingFlag));

const flagsWithoutFreeDelivery = computed(() =>
  offer.marketing_flags.filter((flag) => !isFreeDeliveryMarketingFlag(flag)),
);

function getBadgeTitle(badge: MarketingBadge) {
  if (badge.slug !== 'sale' || !offer.old_price) return badge.title;

  const discount = 100 - (100 / offer.old_price) * offer.price;
  const roundedDiscount = discount < 5 ? Math.ceil(discount) : Math.round(discount / 5) * 5;

  return roundedDiscount > 0 ? `-${roundedDiscount}%` : '';
}
</script>

<template>
  <section class="product-image-badges">
    <component
      :is="freeDelivery.description ? 'button' : 'div'"
      v-if="freeDelivery && shouldShowFreeDeliveryFlag(usedIn, isFreeDeliveryAvailable)"
      :style="{
        'border-color': freeDelivery.border_color ?? '#393d46',
        'background-color': freeDelivery.background_color ?? 'transparent',
        color: freeDelivery.text_color ?? '#393d46',
      }"
      class="product-image-badges__item"
      @click.stop.prevent="selectedFlag = freeDelivery"
    >
      {{ freeDelivery.title }}
    </component>

    <div
      v-for="badge in offer.marketing_badges"
      :key="badge.title"
    >
      <div
        v-if="getBadgeTitle(badge)"
        :style="{
          'border-color': badge.border_color ?? '#393d46',
          'background-color': badge.background_color ?? 'transparent',
          color: badge.text_color ?? '#393d46',
        }"
        class="product-image-badges__item"
      >
        {{ getBadgeTitle(badge) }}
      </div>
    </div>

    <template
      v-for="flag in flagsWithoutFreeDelivery"
      :key="flag.title"
    >
      <component
        :is="flag.description ? 'button' : 'div'"
        v-if="checkFlagDisplay(flag)"
        :style="{
          'border-color': flag.border_color ?? '#393d46',
          'background-color': flag.background_color ?? 'transparent',
          color: flag.text_color ?? '#393d46',
        }"
        class="product-image-badges__item"
        @click.stop.prevent="selectedFlag = flag"
      >
        {{ flag.title }}
      </component>
    </template>

    <BaseModal
      v-model="isShowFlagModal"
      :title="selectedFlag?.title"
      confirm-btn-text="Понятно"
      @close="selectedFlag = null"
      @confirm="selectedFlag = null"
    >
      <div v-html="selectedFlag?.description" />
    </BaseModal>
  </section>
</template>

<style lang="scss" scoped>
.product-image-badges {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &__item {
    width: fit-content;
    padding: 2px;
    border: 2px solid;
    border-radius: 4px;
    font-weight: $medium;
    font-size: $text-2xs;
  }
}
</style>
